import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  return r.json();
}

async function apiPost(path) {
  const r = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include' });
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  return r.json();
}

export default function McpAdmin() {
  const [clients, setClients] = useState([]);
  const [audit, setAudit] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [c, a, m] = await Promise.all([
        apiGet('/api/mcp-admin/clients'),
        apiGet('/api/mcp-admin/audit?limit=50'),
        apiGet('/api/.well-known/oauth-authorization-server'),
      ]);
      setClients(c.clients || []);
      setAudit(a.audit || []);
      setMetadata(m);
      setErr(null);
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function revoke(id) {
    if (!window.confirm(`Revoke client ${id}?`)) return;
    await apiPost(`/api/mcp-admin/clients/${encodeURIComponent(id)}/revoke`);
    refresh();
  }

  return (
    <div data-testid="mcp-admin" style={styles.wrap}>
      <h1 style={styles.h1}>MCP Server · Claude Pro bridge</h1>
      <p style={styles.sub}>
        Exposes Hail Mary as a remote MCP connector for claude.ai (OAuth 2.1 + DCR).
      </p>

      <section style={styles.card} data-testid="mcp-setup">
        <h2 style={styles.h2}>Setup in claude.ai (step-by-step)</h2>
        <ol style={styles.ol}>
          <li>In Claude.ai open <b>Settings → Connectors</b> → <b>Add custom connector</b>.</li>
          <li>Name: <code>Hail Mary</code></li>
          <li>Server URL: <code style={styles.code}>{metadata?.issuer || '…'}/api/mcp</code></li>
          <li>claude.ai will auto-discover the OAuth 2.1 metadata via <code>{'/api/.well-known/oauth-protected-resource'}</code> and launch the consent screen.</li>
          <li>Approve the requested scopes on the consent page — a bearer access_token (1h TTL) is issued automatically.</li>
          <li>Test the connector in a Claude chat: <code>"List articles collected on YYYY-MM-DD using Hail Mary."</code></li>
        </ol>
        <p style={styles.hint}>
          Common errors:<br/>
          · <b>metadata_discovery_failed</b> → check <code>MCP_PUBLIC_BASE_URL</code> in backend/.env matches your public URL.<br/>
          · <b>invalid_redirect_uri</b> → claude.ai hard-codes <code>https://claude.ai/api/mcp/auth_callback</code> — do NOT edit.<br/>
          · <b>insufficient_scope</b> → ensure the client registration requests <code>read:sources read:customers read:taxonomy read:briefs write:briefs offline_access</code>.
        </p>
      </section>

      <section style={styles.card} data-testid="mcp-clients-section">
        <div style={styles.row}>
          <h2 style={styles.h2}>Dynamically Registered OAuth Clients</h2>
          <button onClick={refresh} style={styles.btn} data-testid="mcp-refresh-btn">Refresh</button>
        </div>
        {err && <div style={styles.err}>{err}</div>}
        {loading ? <div style={styles.muted}>Loading…</div> : (
          <table style={styles.table} data-testid="mcp-clients-table">
            <thead><tr>
              <th>Name</th><th>client_id</th><th>Live tokens</th>
              <th>Last used</th><th>Created</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={7} style={styles.muted}>No clients yet. Add a Hail Mary connector in claude.ai to see it here.</td></tr>
              )}
              {clients.map(c => (
                <tr key={c.client_id} data-testid={`mcp-client-${c.client_id}`}>
                  <td>{c.client_name}</td>
                  <td style={styles.mono}>{c.client_id.slice(0, 24)}…</td>
                  <td>{c.live_tokens}</td>
                  <td style={styles.mono}>{c.last_token_at ? new Date(c.last_token_at).toLocaleString() : '—'}</td>
                  <td style={styles.mono}>{new Date(c.created_at).toLocaleString()}</td>
                  <td>{c.revoked_at ? <span style={styles.badgeOff}>revoked</span> : <span style={styles.badgeOn}>active</span>}</td>
                  <td>{!c.revoked_at && (
                    <button onClick={() => revoke(c.client_id)} style={styles.btnRed} data-testid={`revoke-${c.client_id}`}>Revoke</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={styles.card} data-testid="mcp-audit-section">
        <h2 style={styles.h2}>Recent MCP Activity (last 50 tool calls)</h2>
        {!loading && (
          <table style={styles.table} data-testid="mcp-audit-table">
            <thead><tr>
              <th>When</th><th>Client</th><th>Tool</th><th>Status</th>
              <th>Duration</th><th>Summary</th>
            </tr></thead>
            <tbody>
              {audit.length === 0 && <tr><td colSpan={6} style={styles.muted}>No activity yet.</td></tr>}
              {audit.map(a => (
                <tr key={a.id}>
                  <td style={styles.mono}>{new Date(a.created_at).toLocaleTimeString()}</td>
                  <td>{a.client_name || '—'}</td>
                  <td style={styles.mono}>{a.tool_name}</td>
                  <td><span style={a.status === 'success' ? styles.badgeOn : styles.badgeOff}>{a.status}</span></td>
                  <td>{a.duration_ms}ms</td>
                  <td style={styles.summary}>{a.result_summary || a.error_message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={styles.card} data-testid="mcp-scopes-section">
        <h2 style={styles.h2}>Scopes exposed</h2>
        <ul style={styles.ol}>
          <li><code>read:sources</code> — list_sources_for_date, search_articles</li>
          <li><code>read:customers</code> — get_customer_tiers</li>
          <li><code>read:taxonomy</code> — get_taxonomy</li>
          <li><code>read:briefs</code> — get_recent_briefs</li>
          <li><code>write:briefs</code> — push_brief (creates <code>imported_draft</code> rows)</li>
          <li><code>offline_access</code> — refresh tokens (30 days)</li>
        </ul>
      </section>
    </div>
  );
}

const styles = {
  wrap:   { padding: '24px', maxWidth: 1200, margin: '0 auto', color: '#EDEDED', background: '#0A0A0B', minHeight: '100vh' },
  h1:     { fontSize: 24, margin: 0, fontWeight: 600 },
  sub:    { color: '#9c9ca3', fontSize: 13, marginTop: 4, marginBottom: 24 },
  card:   { background: '#141418', border: '1px solid #2a2a30', borderRadius: 12, padding: 20, marginBottom: 20 },
  h2:     { fontSize: 15, color: '#7FFFC4', margin: 0, marginBottom: 12 },
  row:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#c5c5cb' },
  mono:   { fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace', fontSize: 11, color: '#9c9ca3' },
  code:   { fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', color: '#7FFFC4', fontSize: 12, background: '#1b1b22', padding: '2px 6px', borderRadius: 4 },
  ol:     { paddingLeft: 18, color: '#c5c5cb', fontSize: 13, lineHeight: 1.8 },
  hint:   { color: '#9c9ca3', fontSize: 12, marginTop: 12, borderLeft: '2px solid #2a2a30', paddingLeft: 12 },
  muted:  { color: '#6d6d75', padding: '12px 0', textAlign: 'center' },
  err:    { color: '#ff8080', background: '#2b0d0d', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 12 },
  btn:    { background: '#2a2a30', color: '#EDEDED', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnRed: { background: '#8b1a1a', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11 },
  badgeOn:  { background: '#1a3b1a', color: '#7FFFC4', padding: '2px 8px', borderRadius: 10, fontSize: 11 },
  badgeOff: { background: '#3b1a1a', color: '#ff8080', padding: '2px 8px', borderRadius: 10, fontSize: 11 },
  summary:{ color: '#c5c5cb', fontSize: 11, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
